/**
 * @file BackButton.tsx
 * @description 뒤로가기 버튼 컴포넌트
 *
 * 주요 기능:
 * 1. 브라우저 히스토리 뒤로가기
 * 2. 또는 특정 경로로 이동
 * 3. sessionStorage에서 이전 경로 확인하여 자동으로 돌아가기
 *
 * 핵심 구현 로직:
 * - Client Component (useRouter 사용)
 * - href prop이 제공되면 해당 경로로 이동
 * - href가 없으면 sessionStorage에서 이전 경로 확인
 * - 이전 경로가 없으면 router.back() 사용
 * - sessionStorage 키 사용 ("bidix-prev-path")
 *
 * @dependencies
 * - next/navigation: useRouter
 * - lucide-react: ArrowLeft 아이콘
 * - @/components/shared/ui/button: shadcn 버튼 컴포넌트
 */

"use client";

import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/shared/ui/button";
import { cn } from "@/lib/utils/shared/core-utils";

export interface BackButtonProps {
  href?: string;
  label?: string;
  className?: string;
}

export function BackButton({ href, label = "뒤로가기", className }: BackButtonProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [fallbackHref, setFallbackHref] = useState<string | null>(null);

  // 클라이언트 마운트 확인 및 이전 경로 확인
  useEffect(() => {
    setMounted(true);
    if (typeof window !== "undefined") {
      const storedPrevPath = sessionStorage.getItem("bidix-prev-path");
      // 저장된 경로가 현재 경로와 다를 때만 사용
      if (storedPrevPath && storedPrevPath !== pathname) {
        setFallbackHref(storedPrevPath);
      }
    }
  }, [pathname]);

  const handleBack = () => {
    if (href) {
      router.push(href);
    } else if (fallbackHref && fallbackHref !== pathname) {
      router.push(fallbackHref);
    } else {
      router.back();
    }
  };

  // 서버 사이드에서는 href가 있으면 Link 사용
  if (href) {
    return (
      <Link href={href}>
        <Button
          variant="ghost"
          size="sm"
          className={cn("gap-2 font-[var(--font-noto-sans-kr)]", className)}
        >
          <ArrowLeft className="h-4 w-4" />
          {label}
        </Button>
      </Link>
    );
  }

  // 클라이언트 사이드에서만 동적 href 사용 (현재 경로와 다를 때만)
  if (mounted && fallbackHref && fallbackHref !== pathname) {
    return (
      <Link href={fallbackHref}>
        <Button
          variant="ghost"
          size="sm"
          className={cn("gap-2 font-[var(--font-noto-sans-kr)]", className)}
        >
          <ArrowLeft className="h-4 w-4" />
          {label}
        </Button>
      </Link>
    );
  }

  // fallback: 브라우저 히스토리 사용
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleBack}
      className={cn("gap-2 font-[var(--font-noto-sans-kr)]", className)}
    >
      <ArrowLeft className="h-4 w-4" />
      {label}
    </Button>
  );
}

